import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Fade,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getNeonColor, scrollable } from "../../theme";
import { ExpandMore, KeyboardArrowDown, SwapVert } from "@mui/icons-material";
import { useReferentialContext } from "../../Context";
import { ScrimGames, ScrimGameSummaries, ScrimTeamRoundSummaries } from "../../reckon-api";
import { NeonCard, NeonCardContent } from "../../NeonComponents/NeonCards";
import TeamIcon from "../../components/Icons/TeamIcon";
import AgentIcon from "../../components/Icons/AgentIcon";
import RoundEndIcon from "../../components/Icons/RoundEndIcon";
import dayjs from "dayjs";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NormalizedGame {
    id: string;
    date: string;
    map: string;
    myScore: number;
    enemyScore: number;
    myAgents: string;
    enemyAgents: string;
    won: boolean;
    team1: string;
    team2: string;
}

interface ScrimSession {
    dateLabel: string;
    date: string;
    opponent: string;
    games: NormalizedGame[];
    wins: number;
    losses: number;
}

// ---------------------------------------------------------------------------
// Session builder
// ---------------------------------------------------------------------------

function buildSessions(scrims: ScrimGames[], userTeam: string): ScrimSession[] {
    const normalized = scrims
        .filter((g) => g.team1 === userTeam || g.team2 === userTeam)
        .map((g): NormalizedGame & { opponent: string; dayKey: string } => {
            const isTeam1 = g.team1 === userTeam;
            return {
                id: g.id,
                date: g.date,
                dayKey: dayjs(g.date).format("YYYY-MM-DD"),
                map: g.map,
                myScore: isTeam1 ? g.score_team1 : g.score_team2,
                enemyScore: isTeam1 ? g.score_team2 : g.score_team1,
                myAgents: isTeam1 ? g.team1_agents : g.team2_agents,
                enemyAgents: isTeam1 ? g.team2_agents : g.team1_agents,
                won: g.winner === userTeam,
                opponent: isTeam1 ? g.team2 : g.team1,
                team1: g.team1,
                team2: g.team2,
            };
        });

    const grouped = new Map<string, (typeof normalized)[number][]>();
    for (const game of normalized) {
        const key = `${game.dayKey}__${game.opponent}`;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(game);
    }

    const sessions: ScrimSession[] = [];
    for (const [, games] of grouped) {
        const sorted = games.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
        const wins = sorted.filter((g) => g.won).length;
        sessions.push({
            dateLabel: dayjs(sorted[0].date).format("ddd, MMM D YYYY"),
            date: sorted[0].dayKey,
            opponent: sorted[0].opponent,
            games: sorted,
            wins,
            losses: sorted.length - wins,
        });
    }

    return sessions.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function AgentsRow({ agents, justify }: { agents: string; justify: "flex-start" | "flex-end" }) {
    if (!agents) return null;
    return (
        <Stack direction="row" spacing={0.25} sx={{ alignItems: "center", justifyContent: justify }}>
            {agents.split(",").map((agent, i) => (
                <AgentIcon key={i} agent_name={agent.trim()} sx={{ width: 24, height: 24 }} />
            ))}
        </Stack>
    );
}

function StatCell({ value, suffix, bold }: { value: number | null | undefined; suffix?: string; bold?: boolean }) {
    if (value === null || value === undefined) return <Typography variant="body2" sx={{ color: "text.secondary" }}>-</Typography>;
    const display = Number.isInteger(value) ? value : value.toFixed(1);
    return (
        <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, fontSize: "0.8rem" }}>
            {display}{suffix}
        </Typography>
    );
}

// ---------------------------------------------------------------------------
// Round timeline (icon row)
// ---------------------------------------------------------------------------

function RoundTimeline({
    rounds,
    userTeam,
    opponent,
}: {
    rounds: ScrimTeamRoundSummaries[];
    userTeam: string;
    opponent: string;
}) {
    const myRounds = rounds
        .filter((r) => {
            const players = r.players || "";
            return players.toLowerCase().includes(userTeam.toLowerCase().split("_")[0].toLowerCase());
        })
        .sort((a, b) => a.round_index - b.round_index);

    const displayRounds = myRounds.length > 0 ? myRounds : rounds.slice(0, Math.floor(rounds.length / 2)).sort((a, b) => a.round_index - b.round_index);

    if (displayRounds.length === 0) return null;

    const hasHalves = displayRounds.length > 12;

    const roundIcon = (r: ScrimTeamRoundSummaries, won: boolean) => (
        <Tooltip title={`R${r.round_index + 1} - ${r.side ?? ""} - ${won ? "Won" : "Lost"}`}>
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: won ? "rgba(46, 204, 113, 0.2)" : "rgba(231, 76, 60, 0.2)",
                    border: `1.5px solid ${won ? "rgba(46, 204, 113, 0.5)" : "rgba(231, 76, 60, 0.5)"}`,
                }}
            >
                <RoundEndIcon round_end={r.round_end ?? undefined} sx={{ width: 18, height: 18 }} />
            </Box>
        </Tooltip>
    );

    const emptySlot = <Box sx={{ width: 28, height: 28 }} />;

    const firstHalf = displayRounds.slice(0, 12);
    const secondHalf = displayRounds.slice(12);

    const renderHalfColumn = (half: ScrimTeamRoundSummaries[]) => (
        <Stack direction="column" spacing={0.25}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                {half.map((r) => (r.win === 1 ? <React.Fragment key={r.id}>{roundIcon(r, true)}</React.Fragment> : <React.Fragment key={r.id}>{emptySlot}</React.Fragment>))}
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                {half.map((r) => (r.win !== 1 ? <React.Fragment key={r.id}>{roundIcon(r, false)}</React.Fragment> : <React.Fragment key={r.id}>{emptySlot}</React.Fragment>))}
            </Stack>
        </Stack>
    );

    return (
        <Stack direction="row" spacing={0} sx={{ alignItems: "center", justifyContent: "center", py: 0.5 }}>
            {/* Team icons column */}
            <Stack direction="column" spacing={0.25} sx={{ mr: 0.75 }}>
                <Box sx={{ height: 28, display: "flex", alignItems: "center" }}>
                    <TeamIcon team_name={userTeam} sx={{ width: 22, height: 22 }} />
                </Box>
                <Box sx={{ height: 28, display: "flex", alignItems: "center" }}>
                    <TeamIcon team_name={opponent} sx={{ width: 22, height: 22 }} />
                </Box>
            </Stack>

            {/* First half */}
            {renderHalfColumn(firstHalf)}

            {/* Swap icon spanning both rows */}
            {hasHalves && (
                <Tooltip title="Side swap">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mx: 0.75, alignSelf: "stretch" }}>
                        <SwapVert sx={{ fontSize: 28, color: "rgba(255,255,255,0.4)" }} />
                    </Box>
                </Tooltip>
            )}

            {/* Second half */}
            {hasHalves && renderHalfColumn(secondHalf)}
        </Stack>
    );
}

// ---------------------------------------------------------------------------
// Player scoreboard row
// ---------------------------------------------------------------------------

const statHeaders = ["Player", "Agent", "K", "D", "A", "ACS", "ADR", "KAST", "HS%", "FK", "FD", "Rating"];
const statWidths = [130, 40, 35, 35, 35, 50, 50, 55, 50, 35, 35, 55];

function PlayerRow({ summary }: { summary: ScrimGameSummaries }) {
    return (
        <Stack
            direction="row"
            sx={{
                alignItems: "center",
                py: 0.5,
                px: 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                borderRadius: 0.5,
            }}
        >
            <Box sx={{ width: statWidths[0], minWidth: statWidths[0], overflow: "hidden" }}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {summary.player}
                </Typography>
            </Box>
            <Box sx={{ width: statWidths[1], minWidth: statWidths[1], display: "flex", justifyContent: "center" }}>
                <AgentIcon agent_name={summary.agent} sx={{ width: 24, height: 24 }} />
            </Box>
            <Box sx={{ width: statWidths[2], textAlign: "center" }}><StatCell value={summary.kills} bold /></Box>
            <Box sx={{ width: statWidths[3], textAlign: "center" }}><StatCell value={summary.deaths} /></Box>
            <Box sx={{ width: statWidths[4], textAlign: "center" }}><StatCell value={summary.assists} /></Box>
            <Box sx={{ width: statWidths[5], textAlign: "center" }}><StatCell value={summary.acs} /></Box>
            <Box sx={{ width: statWidths[6], textAlign: "center" }}><StatCell value={summary.adr} /></Box>
            <Box sx={{ width: statWidths[7], textAlign: "center" }}><StatCell value={summary.kast != null ? summary.kast * 100 : null} suffix="%" /></Box>
            <Box sx={{ width: statWidths[8], textAlign: "center" }}><StatCell value={summary.headshot_percent != null ? summary.headshot_percent * 100 : null} suffix="%" /></Box>
            <Box sx={{ width: statWidths[9], textAlign: "center" }}><StatCell value={summary.first_kills} /></Box>
            <Box sx={{ width: statWidths[10], textAlign: "center" }}><StatCell value={summary.first_deaths} /></Box>
            <Box sx={{ width: statWidths[11], textAlign: "center" }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: (summary.rating_hltv ?? 0) >= 1.0 ? "lightgreen" : "error.main",
                    }}
                >
                    {summary.rating_hltv != null ? summary.rating_hltv.toFixed(2) : "-"}
                </Typography>
            </Box>
        </Stack>
    );
}

function ScoreboardHeader() {
    return (
        <Stack direction="row" sx={{ px: 1, pb: 0.25, opacity: 0.5 }}>
            {statHeaders.map((h, i) => (
                <Box key={h} sx={{ width: statWidths[i], minWidth: statWidths[i], textAlign: i < 2 ? "left" : "center" }}>
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>{h}</Typography>
                </Box>
            ))}
        </Stack>
    );
}

// ---------------------------------------------------------------------------
// Game details panel (lazy loaded)
// ---------------------------------------------------------------------------

function GameDetails({
    gameId,
    game,
    userTeam,
    neonColor,
}: {
    gameId: string;
    game: NormalizedGame;
    userTeam: string;
    neonColor: string;
}) {
    const ctx = useReferentialContext();
    const [summaries, setSummaries] = useState<ScrimGameSummaries[] | null>(null);
    const [rounds, setRounds] = useState<ScrimTeamRoundSummaries[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            ctx.reckonApi.scrimGameSummaries.list({
                additional_filters: { game_id: gameId },
                ordering: ["-acs"],
            }),
            ctx.reckonApi.scrimTeamRoundSummaries.list({
                game: gameId,
                ordering: ["round_index"],
            }),
        ])
            .then(([sums, rnds]) => {
                setSummaries(sums);
                setRounds(rnds);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [gameId]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: neonColor }} />
            </Box>
        );
    }

    if (!summaries || summaries.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
                No detailed stats available for this game.
            </Typography>
        );
    }

    const myPlayers = summaries.filter((s) => s.win === game.won).sort((a, b) => (b.acs ?? 0) - (a.acs ?? 0));
    const enemyPlayers = summaries.filter((s) => s.win !== game.won).sort((a, b) => (b.acs ?? 0) - (a.acs ?? 0));
    const opponent = game.team1 === userTeam ? game.team2 : game.team1;

    return (
        <Fade in timeout={400}>
            <Box sx={{ px: 1 }}>
                {/* Round timeline */}
                {rounds && rounds.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                            Round timeline
                        </Typography>
                        <RoundTimeline rounds={rounds} userTeam={userTeam} opponent={opponent} />
                    </Box>
                )}

                {/* Both scoreboards side by side */}
                <Stack direction="row" spacing={3} sx={{ width: "100%", overflow: "auto" }}>
                    {/* My team scoreboard */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.75 }}>
                            <TeamIcon team_name={userTeam} sx={{ width: 20, height: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                {userTeam.split("_")[0]}
                            </Typography>
                            <Chip
                                label={game.won ? "WIN" : "LOSS"}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    backgroundColor: game.won ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)",
                                    color: game.won ? "#4caf50" : "#f44336",
                                }}
                            />
                        </Stack>
                        <ScoreboardHeader />
                        {myPlayers.map((p) => (
                            <PlayerRow key={p.id} summary={p} />
                        ))}
                    </Box>

                    {/* Enemy team scoreboard */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.75 }}>
                            <TeamIcon team_name={opponent} sx={{ width: 20, height: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                {opponent.split("_")[0]}
                            </Typography>
                            <Chip
                                label={game.won ? "LOSS" : "WIN"}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    backgroundColor: game.won ? "rgba(244,67,54,0.2)" : "rgba(76,175,80,0.2)",
                                    color: game.won ? "#f44336" : "#4caf50",
                                }}
                            />
                        </Stack>
                        <ScoreboardHeader />
                        {enemyPlayers.map((p) => (
                            <PlayerRow key={p.id} summary={p} />
                        ))}
                    </Box>
                </Stack>
            </Box>
        </Fade>
    );
}

// ---------------------------------------------------------------------------
// Game row (with expandable details)
// ---------------------------------------------------------------------------

function GameRow({
    game,
    neonColor,
    userTeam,
    defaultExpanded,
}: {
    game: NormalizedGame;
    neonColor: string;
    userTeam: string;
    defaultExpanded: boolean;
}) {
    const [detailsOpen, setDetailsOpen] = useState(defaultExpanded);

    return (
        <Box>
            <Stack
                direction="row"
                onClick={() => setDetailsOpen((p) => !p)}
                sx={{
                    alignItems: "center",
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    backgroundColor: game.won ? "rgba(76, 175, 80, 0.06)" : "rgba(244, 67, 54, 0.06)",
                    borderLeft: `3px solid ${game.won ? "#4caf50" : "#f44336"}`,
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    "&:hover": {
                        backgroundColor: game.won ? "rgba(76, 175, 80, 0.12)" : "rgba(244, 67, 54, 0.12)",
                    },
                }}
            >
                <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                    <AgentsRow agents={game.myAgents} justify="flex-end" />
                </Box>

                <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: "center", justifyContent: "center", minWidth: 90, mx: 2 }}
                >
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: game.won ? "lightgreen" : "error.main", minWidth: 20, textAlign: "right" }}>
                        {game.myScore}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>-</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: !game.won ? "lightgreen" : "error.main", minWidth: 20, textAlign: "left" }}>
                        {game.enemyScore}
                    </Typography>
                </Stack>

                <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                    <AgentsRow agents={game.enemyAgents} justify="flex-start" />
                </Box>

                <Chip
                    label={game.map}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 2, minWidth: 70, textTransform: "capitalize", fontSize: "0.75rem" }}
                />

                <KeyboardArrowDown
                    sx={{
                        ml: 1,
                        fontSize: 18,
                        color: "text.secondary",
                        transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s",
                    }}
                />
            </Stack>

            <Collapse in={detailsOpen} unmountOnExit>
                <Box
                    sx={{
                        ml: "3px",
                        borderLeft: `3px solid ${game.won ? "rgba(76,175,80,0.3)" : "rgba(244,67,54,0.3)"}`,
                        px: 2,
                        py: 1.5,
                        backgroundColor: "rgba(0,0,0,0.15)",
                        borderRadius: "0 0 4px 4px",
                    }}
                >
                    <GameDetails gameId={game.id} game={game} userTeam={userTeam} neonColor={neonColor} />
                </Box>
            </Collapse>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Session card
// ---------------------------------------------------------------------------

function SessionCard({
    session,
    userTeam,
    neonColor,
    mostRecentGameId,
}: {
    session: ScrimSession;
    userTeam: string;
    neonColor: string;
    mostRecentGameId: string | null;
}) {
    const [expanded, setExpanded] = useState(true);
    const sessionWon = session.wins > session.losses;
    const tied = session.wins === session.losses;

    return (
        <NeonCard
            glow_color={sessionWon ? "#4caf50" : tied ? undefined : "#f44336"}
            sx={{ width: "100%" }}
        >
            <Box
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { backgroundColor: "action.hover" },
                    transition: "background-color 0.2s",
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", minWidth: 140, fontSize: "0.85rem" }}
                >
                    {session.dateLabel}
                </Typography>

                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}
                >
                    <TeamIcon team_name={userTeam} sx={{ width: 32, height: 32 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {userTeam.split("_")[0]}
                    </Typography>

                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: sessionWon ? "lightgreen" : tied ? "text.primary" : "error.main" }}>
                            {session.wins}
                        </Typography>
                        <Typography sx={{ color: "text.secondary" }}>-</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: !sessionWon ? (tied ? "text.primary" : "lightgreen") : "error.main" }}>
                            {session.losses}
                        </Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {session.opponent.split("_")[0]}
                    </Typography>
                    <TeamIcon team_name={session.opponent} sx={{ width: 32, height: 32 }} />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 90, justifyContent: "flex-end" }}>
                    <Chip
                        label={`${session.games.length} game${session.games.length > 1 ? "s" : ""}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                    />
                    <ExpandMore
                        sx={{
                            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.3s",
                            color: "text.secondary",
                            fontSize: 20,
                        }}
                    />
                </Stack>
            </Box>

            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Stack
                        direction="row"
                        sx={{ alignItems: "center", pb: 0.75, px: 2, opacity: 0.5 }}
                    >
                        <Typography variant="caption" sx={{ flex: 1, textAlign: "right" }}>
                            {userTeam.split("_")[0]}
                        </Typography>
                        <Typography variant="caption" sx={{ minWidth: 90, textAlign: "center", mx: 2 }}>
                            Score
                        </Typography>
                        <Typography variant="caption" sx={{ flex: 1, textAlign: "left" }}>
                            {session.opponent.split("_")[0]}
                        </Typography>
                        <Box sx={{ ml: 2, minWidth: 89 }} />
                    </Stack>

                    <Stack spacing={0.75}>
                        {session.games.map((game) => (
                            <GameRow
                                key={game.id}
                                game={game}
                                neonColor={neonColor}
                                userTeam={userTeam}
                                defaultExpanded={game.id === mostRecentGameId}
                            />
                        ))}
                    </Stack>
                </Box>
            </Collapse>
        </NeonCard>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ScrimsHistory() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const userTeam = ctx.user.team;

    const [scrims, setScrims] = useState<ScrimGames[]>([]);
    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        ctx.reckonApi.scrimGames
            .list({ ordering: ["-date"] })
            .then((result) => {
                setScrims(result);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    const sessions = useMemo(() => buildSessions(scrims, userTeam), [scrims, userTeam]);

    const mostRecentGameId = useMemo(() => {
        if (sessions.length === 0) return null;
        const latestSession = sessions[0];
        return latestSession.games[latestSession.games.length - 1].id;
    }, [sessions]);

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                p: { xs: 1, sm: 2, md: 3 },
                ...scrollable,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Scrims History
            </Typography>

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : sessions.length === 0 ? (
                <NeonCard>
                    <NeonCardContent>
                        <Typography sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                            No scrim games found for {userTeam}
                        </Typography>
                    </NeonCardContent>
                </NeonCard>
            ) : (
                <Stack spacing={2}>
                    {sessions.map((session) => (
                        <SessionCard
                            key={`${session.date}__${session.opponent}`}
                            session={session}
                            userTeam={userTeam}
                            neonColor={neonColor}
                            mostRecentGameId={mostRecentGameId}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}
